import { useState } from 'react'
import { ChartFrame, ChartTooltip, type TooltipState, ValueAxis } from './Chart'
import {
  PLOT,
  type Series,
  axisNumber,
  ceiling,
  inner,
  labelStride,
  niceTicks,
  seriesColor
} from './scale'

/** The surface gap that separates touching segments. Never a stroke: a border is ink that is not data. */
const GAP = 2
const MAXIMUM_BAR = 24

/**
 * A total per day, split into the parts it is made of.
 *
 * Stacked rather than grouped because the question is "how much, and how much of it was trouble" —
 * the total has meaning here, and grouped columns make a total something the reader has to add up.
 * The parts are ordered with the one that matters most at the bottom, against the baseline, because
 * only the bottom segment can be compared across columns by eye.
 */
export function StackedBarChart({
  title,
  description,
  categories,
  labels,
  series
}: {
  title: string
  description: string
  categories: string[]
  labels: string[]
  series: Series[]
}) {
  const [hover, setHover] = useState<TooltipState | null>(null)
  const box = inner(PLOT)

  const totals = categories.map((_, index) =>
    series.reduce((sum, entry) => sum + (entry.values[index] ?? 0), 0)
  )
  const highest = Math.max(1, ...totals)
  const top = ceiling(highest)
  const ticks = niceTicks(highest)

  const slot = box.width / Math.max(1, categories.length)
  const barWidth = Math.min(MAXIMUM_BAR, slot * 0.62)
  const stride = labelStride(categories.length, box.width)
  const scale = (value: number) => box.bottom - (value / top) * box.height

  return (
    <ChartFrame title={title} description={description} categories={categories} series={series}>
      <div className="chart-surface">
        <svg
          viewBox={`0 0 ${PLOT.width} ${PLOT.height}`}
          preserveAspectRatio="xMidYMid meet"
          className="chart-svg"
          onPointerLeave={() => setHover(null)}
        >
          <ValueAxis ticks={ticks} scale={scale} left={box.left} right={box.right} format={axisNumber} />

          {categories.map((category, index) => {
            const center = box.left + slot * (index + 0.5)
            const x = center - barWidth / 2
            let cursor = box.bottom

            return (
              <g
                key={category}
                onPointerEnter={() =>
                  setHover({
                    x: center / PLOT.width,
                    y: scale(totals[index] ?? 0) / PLOT.height,
                    heading: category,
                    rows: series.map((entry, position) => ({
                      label: entry.label,
                      value: entry.values[index] ?? 0,
                      color: seriesColor(position)
                    }))
                  })
                }
              >
                {/* The whole column, floor to ceiling, is the hit target. */}
                <rect x={center - slot / 2} y={box.top} width={slot} height={box.height} fill="transparent" />

                {series.map((entry, position) => {
                  const value = entry.values[index] ?? 0
                  if (value <= 0) return null

                  const full = (value / top) * box.height
                  // The gap is taken out of the segment rather than drawn over it, so a stack of
                  // segments still adds up to the height its total deserves.
                  const drawn = Math.max(1, full - (position > 0 ? GAP : 0))
                  const y = cursor - drawn
                  cursor -= full

                  return (
                    <rect
                      key={entry.key}
                      x={x}
                      y={y}
                      width={barWidth}
                      height={drawn}
                      fill={seriesColor(position)}
                    />
                  )
                })}
              </g>
            )
          })}

          <g className="chart-axis" aria-hidden="true">
            {labels.map((label, index) =>
              index % stride === 0 ? (
                <text
                  key={`${label}-${index}`}
                  x={box.left + slot * (index + 0.5)}
                  y={box.bottom + 18}
                  textAnchor="middle"
                  className="chart-tick"
                >
                  {label}
                </text>
              ) : null
            )}
          </g>
        </svg>
        <ChartTooltip state={hover} />
      </div>
    </ChartFrame>
  )
}
